import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import styled from "styled-components"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import { useAllDataPlans } from "../../../hooks/useAllDataPlans"
import { formatAllDataPlans } from "../../../utils/formatters"
import { DataPlanModel } from "../../../utils/models"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import SearchField from "../../atoms/inputs/SearchField"
import Dropdown, { Option, optionsMap } from "../../atoms/inputs/Dropdown"
import IconButton from "../../atoms/buttons/IconButton"
import ListIcon from "../../atoms/icons/ListIcon"
import { theme } from "../../theme"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import { Link, useNavigate, useParams } from "react-router-dom"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import PaginationComponent from "../../molecules/PaginationComponent"
import DataPlanOverviewCard from "../../organisms/DataPlanOverviewCard"
import EmptyDashboardPage from "./EmptyDashboardPage"
import NoSearchResultPage from "./NoSearchResultPage"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 2rem;
`

const dropdownSelectItems: Option<string>[] = [
  { id: "alphabetical", value: "alphabetical", name: "Alphabetical A - Z" },
  { id: "-alphabetical", value: "-alphabetical", name: "Alphabetical Z - A" },
  { id: "data_usage", value: "data_usage", name: "Data Usage low - high" },
  { id: "-data_usage", value: "-data_usage", name: "Data Usage high - low" },
]

interface Props {
  suborgFilter: Array<number> | undefined
}

const DataPlansPage: React.FunctionComponent<Props> = ({ suborgFilter }) => {
  const { page } = useParams()
  const navigate = useNavigate()

  const [cardsOpen, setCardsOpen] = React.useState<Array<string>>([])
  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setoffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * PAGINATION_VALUE : 0)
  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [dropdownSelectedId, setDropdownSelectedId] = React.useState<Key>("alphabetical")

  const dropdownSelected = optionsMap(dropdownSelectItems)[dropdownSelectedId].value!

  //Fetch data plans
  const { allDataPlans, error, isLoading } = useAllDataPlans(
    PAGINATION_VALUE,
    offset,
    searchValue,
    suborgFilter,
    dropdownSelected,
  )
  const formattedAllDataPlans = allDataPlans && formatAllDataPlans(allDataPlans.edges, suborgFilter?.length === 0)

  const [dataPlans, setDataPlans] = React.useState<Array<DataPlanModel>>([])

  React.useMemo(() => {
    setDataPlans(formattedAllDataPlans ?? [])
  }, [allDataPlans])

  //Handle open sections
  const checkCardIsOpen = (id: string) => {
    return cardsOpen.includes(id)
  }
  const onClickCard = (id: string) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allDataPlansIds: Array<string> = []
  formattedAllDataPlans?.map((t: any) => allDataPlansIds.push(t.id))
  const onClickDetailsView = () => {
    setCardsOpen(allDataPlansIds)
  }

  //Handle pagination
  const totalCount = allDataPlans?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / PAGINATION_VALUE)
  const totalPages = totalCount < PAGINATION_VALUE ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setoffset(offset ? PAGINATION_VALUE * (newPage - 1) : PAGINATION_VALUE)
      setCurrentPage(newPage)
      navigate(`/dashboard/${newPage}`)
    }
  }

  if (error) {
    captureExceptionWithContext(error, "error fetching data plans")
  }

  React.useEffect(() => {
    document.title = "Dashboard"
  }, [])

  const noDataPlans = dataPlans.length < 1
  const noSearch = searchValue === undefined || searchValue === ""
  const noFilter = suborgFilter === undefined || suborgFilter.length < 1

  return (
    <Container>
      <HorizontalSpacer spacing={4} />
      <Row>
        <Col lg={6} md={12} xs={12}>
          <Row justify="between" align="end">
            <Col md={4}>
              <SearchField onChange={(e) => setSearchValue(e)} />
              <Visible xs>
                <HorizontalSpacer spacing={4} />
              </Visible>
            </Col>
            <Col md={4} xs={6}>
              <Dropdown
                label="Sort by"
                options={dropdownSelectItems}
                onSelectionChange={(id) => setDropdownSelectedId(id)}
                selectedKey={dropdownSelectedId}
              />
            </Col>
            <Col md={4} xs={6} style={{ display: "flex" }}>
              <div style={{ display: "flex" }}>
                <Button
                  iconLeft={
                    <ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />
                  }
                  onPress={onClickListView}
                  variant={cardsOpen.length < 1 ? "dark" : "light"}
                />
                <Spacer spacing={1} />
                <Visible xs>
                  <Spacer spacing={1} />
                </Visible>
                <Button
                  onPress={onClickDetailsView}
                  variant={cardsOpen.length === allDataPlansIds.length ? "dark" : "light"}
                  iconLeft={
                    <DetailsIcon
                      small
                      color={cardsOpen.length === allDataPlansIds.length ? theme.colors.white : theme.colors.greyDark}
                    />
                  }
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Hidden xs md>
          <Col lg={6} md={8}>
            <Row justify="end" nogutter>
              <SecondaryButton title="Data plans" />
              <Spacer spacing={1} />
              <Link to="/dashboard/groups">
                <PrimaryButton title="Groups" />
              </Link>
              <Spacer spacing={1} />
              <Link to="/dashboard/terminals">
                <PrimaryButton title="Terminals" />
              </Link>
            </Row>
          </Col>
        </Hidden>
      </Row>
      <Visible xs md>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <HorizontalSpacer spacing={2} />
          <SecondaryButton title="Data plans" fullWidth />
          <HorizontalSpacer spacing={2} />

          <Link to="/dashboard/groups">
            <PrimaryButton title="Groups" fullWidth />
          </Link>
          <HorizontalSpacer spacing={2} />

          <Link to="/dashboard/terminals">
            <PrimaryButton title="Terminals" fullWidth />
          </Link>
        </div>
      </Visible>
      <HorizontalSpacer spacing={3} />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div>
          {noDataPlans && noFilter && noSearch ? (
            <EmptyDashboardPage />
          ) : noDataPlans ? (
            <NoSearchResultPage />
          ) : (
            dataPlans.map((dataplan: DataPlanModel) => (
              <DataPlanOverviewCard
                dataplan={dataplan}
                key={dataplan.id}
                cardOpen={checkCardIsOpen(dataplan.id)}
                onClickCard={onClickCard}
              />
            ))
          )}
        </div>
      )}
      <HorizontalSpacer spacing={3} />
      {dataPlans.length > 0 && (
        <PaginationContainer>
          <PaginationComponent
            itemsPerPageValues={PAGINATION_VALUE_PRESETS}
            pages={totalPages}
            currentPageIndex={currentPage}
            onClickPage={onClickPage}
          />
        </PaginationContainer>
      )}
      <HorizontalSpacer spacing={3} />
    </Container>
  )
}

export default DataPlansPage
